import { MULTI_INPUT_PLACEHOLDER_TEXTS, VALUE_TYPE_BEHAVIOR_MAP } from '../../constants/links';
import { LinkTemplateFieldValueType } from '../../types/linkTypes';
import { VALUE_TYPE } from './../../constants/links/valueType';

type LinkDataValueTypes = {
  empty: boolean;
  suggestions: boolean;
  choose: boolean;
  fixed: boolean;
};

type ValueType = 'fixed' | 'suggestions' | 'choose' | 'empty' | 'any';

export class LinkTemplateRulesService {
  public static getOriginalWebUrlInputPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.nameLinkTemplate?.originalWebURL?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.ORIGINAL_WEB_URL;
  };

  public static getOriginalWebUrlDropdownPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.nameLinkTemplate?.originalWebURL?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.SELECT;
  };

  public static getLinkDataValueType = (valueTypes: LinkDataValueTypes): string => {
    if (valueTypes.empty) {
      return VALUE_TYPE.EMPTY;
    }
    if (valueTypes.suggestions) {
      return VALUE_TYPE.SUGGESTIONS;
    }
    if (valueTypes.choose) {
      return VALUE_TYPE.CHOOSE;
    }

    if (valueTypes.fixed) {
      return VALUE_TYPE.FIXED;
    }

    return '';
  };

  public static getLinkDataPlaceholder = (valueTypes): string => {
    const valueType = this.getLinkDataValueType(valueTypes);

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.LINK_DATA;
  };

  public static getRedirectsInputPlaceholder = (valueType: ValueType | undefined): string => {
    if (!valueType) {
      return MULTI_INPUT_PLACEHOLDER_TEXTS.WEB_URL;
    }

    const behavior = VALUE_TYPE_BEHAVIOR_MAP[valueType];

    return (behavior as { placeHolder: string })?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.WEB_URL;
  };

  public static getRedirectsDropdownPlaceholder = (valueType: ValueType | undefined): string => {
    if (!valueType) {
      return MULTI_INPUT_PLACEHOLDER_TEXTS.SELECT;
    }

    const behavior = VALUE_TYPE_BEHAVIOR_MAP[valueType];

    return (behavior as { placeHolder: string })?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.SELECT;
  };

  public static getChannelPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.analyticsTags?.channel?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.CHANNEL;
  };

  public static getCampaignPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.analyticsTags?.campaign?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.CAMPAIGN;
  };

  public static getFeaturePlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.analyticsTags?.feature?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.FEATURE;
  };

  public static getTagsPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.analyticsTags?.tags?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.TAGS;
  };

  public static getSocialMediaTagsTitleInputPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.socialMediaTags?.title?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.TITLE;
  };

  public static getSocialMediaTagsTitleDropdownPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.socialMediaTags?.title?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.SELECT;
  };

  public static getSocialMediaTagsDescriptionInputPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.socialMediaTags?.description?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.DESCRIPTION;
  };

  public static getSocialMediaTagsDescriptionDropdownPlaceholder = (
    templateRules: Record<string, any> | undefined
  ): string => {
    const valueType = templateRules?.socialMediaTags?.description?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.SELECT;
  };

  public static getSocialMediaTagsImageUrlInputPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.socialMediaTags?.imageUrl?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.IMAGE_URL;
  };

  public static getSocialMediaTagsImageUrlDropdownPlaceholder = (templateRules: Record<string, any> | undefined): string => {
    const valueType = templateRules?.socialMediaTags?.imageUrl?.valueType;

    return VALUE_TYPE_BEHAVIOR_MAP[valueType]?.placeHolder || MULTI_INPUT_PLACEHOLDER_TEXTS.SELECT;
  };

  public static isTagsRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.analyticsTags?.tags?.required;
  };

  public static isCampaignRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.analyticsTags?.campaign?.required;
  };

  public static isChannelRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.analyticsTags?.channel?.required;
  };

  public static webUrlHasValue = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.nameLinkTemplate?.originalWebURL?.value;
  };

  public static isWebUrlTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.originalWebURL?.valueType?.toLowerCase() === 'fixed';
  };

  public static isWebUrlTypeEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.originalWebURL?.valueType?.toLowerCase() === 'empty';
  };

  public static isWebUrlTypeSuggestions = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.originalWebURL?.valueType?.toLowerCase() === 'suggestions';
  };

  public static isWebUrlTypeChoose = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.originalWebURL?.valueType?.toLowerCase() === 'choose';
  };

  public static isWebUrlRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.nameLinkTemplate?.originalWebURL?.required;
  };

  public static isPartnerRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.nameLinkTemplate?.linkPartner?.required;
  };

  public static isAdPartnerTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.adPartner?.valueType?.toLowerCase() === 'fixed';
  };

  public static isPartnerTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.linkPartner?.valueType?.toLowerCase() === 'fixed';
  };

  public static isRedirectAndroidRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.redirects?.android?.required;
  };

  public static isRedirectAndroidTypeSuggestions = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.android?.valueType?.toLowerCase() === 'suggestions';
  };

  public static isRedirectAndroidTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.android?.valueType?.toLowerCase() === 'fixed';
  };

  public static isRedirectAndroidTypeEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.android?.valueType?.toLowerCase() === 'empty';
  };

  public static isRedirectAndroidTypeAny = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.android?.valueType?.toLowerCase() === 'any';
  };

  public static isAndroidRedirectTypeAny = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.android?.redirectType?.toLowerCase() === 'any';
  };

  public static isRedirectAndroidTypeChoose = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.android?.valueType?.toLowerCase() === 'choose';
  };

  public static isRedirectIosRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.redirects?.ios?.required;
  };

  public static isRedirectIosTypeSuggestions = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.ios?.valueType?.toLowerCase() === 'suggestions';
  };

  public static isRedirectIosTypeChoose = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.ios?.valueType?.toLowerCase() === 'choose';
  };

  public static isRedirectIosTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.ios?.valueType?.toLowerCase() === 'fixed';
  };

  public static isRedirectIosTypeAny = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.ios?.valueType?.toLowerCase() === 'any';
  };

  public static isRedirectIosTypeEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.ios?.valueType?.toLowerCase() === 'empty';
  };

  public static isIosRedirectTypeAny = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.ios?.redirectType?.toLowerCase() === 'any';
  };

  public static isRedirectDesktopRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.redirects?.desktop?.required;
  };

  public static isRedirectDesktopTypeAny = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.desktop?.valueType?.toLowerCase() === 'any';
  };

  public static isDesktopRedirectTypeAny = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.desktop?.redirectType?.toLowerCase() === 'any';
  };

  public static isDesktopRedirectTypeEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.desktop?.valueType?.toLowerCase() === 'empty';
  };

  public static isRedirectDesktopTypeSuggestions = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.desktop?.valueType?.toLowerCase() === 'suggestions';
  };

  public static isRedirectDesktopTypeChoose = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.desktop?.valueType?.toLowerCase() === 'choose';
  };

  public static isRedirectDesktopTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.redirects?.desktop?.valueType?.toLowerCase() === 'fixed';
  };

  public static isChannelTypeEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.channel?.valueType?.toLowerCase() === 'empty';
  };

  public static isChannelTypeSuggestions = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.channel?.valueType?.toLowerCase() === 'suggestions';
  };

  public static isChannelTypeChoose = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.channel?.valueType?.toLowerCase() === 'choose';
  };

  public static isChannelTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.channel?.valueType?.toLowerCase() === 'fixed';
  };

  public static isCampaignTypeEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.campaign?.valueType?.toLowerCase() === 'empty';
  };

  public static isCampaignTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.campaign?.valueType?.toLowerCase() === 'fixed';
  };

  public static isCampaignTypeSuggestions = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.campaign?.valueType?.toLowerCase() === 'suggestions';
  };

  public static isCampaignTypeChoose = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.campaign?.valueType?.toLowerCase() === 'choose';
  };

  public static isTagsTypeChoose = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.tags?.valueType?.toLowerCase() === 'choose';
  };

  public static isTagsTypeEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.tags?.valueType?.toLowerCase() === 'empty';
  };

  public static isTagsTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.tags?.valueType?.toLowerCase() === 'fixed';
  };

  public static isTagsTypeSuggestions = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.analyticsTags?.tags?.valueType?.toLowerCase() === 'suggestions';
  };

  public static isLinkDataEmpty = (templateRules: Record<string, any> | undefined): boolean => {
    return !templateRules?.linkData?.length;
  };

  public static isAliasRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.nameLinkTemplate?.linkAlias?.required;
  };

  public static isAliasTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.linkAlias?.valueType?.toLowerCase() === 'fixed';
  };

  public static isOsTypeFixed = (templateRules: Record<string, any> | undefined): boolean => {
    return templateRules?.nameLinkTemplate?.osType?.valueType?.toLowerCase() === 'fixed';
  };

  public static isOsTypeRequired = (templateRules: Record<string, any> | undefined): boolean => {
    return !!templateRules?.nameLinkTemplate?.osType?.required;
  };

  public static isSelectable = (valueType: LinkTemplateFieldValueType | ValueType): boolean => {
    return [LinkTemplateFieldValueType.SUGGESTIONS, LinkTemplateFieldValueType.CHOOSE].includes(valueType as any);
  };

  public static isValueTypeSuggestions = (valueType: LinkTemplateFieldValueType | ValueType): boolean => {
    return LinkTemplateFieldValueType.SUGGESTIONS === valueType;
  };

  public static isValueTypeChoose = (valueType: LinkTemplateFieldValueType | ValueType): boolean => {
    return LinkTemplateFieldValueType.CHOOSE === valueType;
  };

  public static isValueTypeAny = (valueType: LinkTemplateFieldValueType | ValueType): boolean => {
    return LinkTemplateFieldValueType.ANY === valueType;
  };
}
